import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState, type ReactNode } from "react";
import { BadgePercent, Loader2, Minus, Plus, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getQuoteByToken, createQuoteCheckout } from "@/lib/quotes.functions";
import { COUNTRIES, DEFAULT_COUNTRY, formatMoney } from "@/lib/ecom/catalog";
import logoSvg from "@/assets/banana-logo.svg";
import imgMetal from "@/assets/products/custom-black-metal.webp";
import imgGold from "@/assets/products/custom-gold.webp";
import imgBamboo from "@/assets/products/custom-bamboo.webp";
import imgWhitePvc from "@/assets/products/custom-white-pvc.webp";
import imgBlackPvc from "@/assets/products/custom-black-pvc.webp";
import imgGoogle from "@/assets/products/retail-google-review.webp";

const IMAGES: Record<string, string> = {
  metal: imgMetal,
  "24kt-gold": imgGold,
  bamboo: imgBamboo,
  "white-pvc": imgWhitePvc,
  "black-pvc": imgBlackPvc,
  "google-review": imgGoogle,
};

const quoteQuery = (token: string) =>
  queryOptions({
    queryKey: ["custom-quote", token],
    queryFn: () => getQuoteByToken({ data: { token } }),
    staleTime: 30_000,
  });

const TOKEN_RE = /^[A-Za-z0-9_-]{32,128}$/;

export const Route = createFileRoute("/quote/$token")({
  loader: ({ params, context }) =>
    TOKEN_RE.test(params.token)
      ? context.queryClient.ensureQueryData(quoteQuery(params.token))
      : { status: "not_found" as const },
  head: () => ({
    meta: [
      { title: "Your custom quote | Banana Cards" },
      { name: "description", content: "Your personal Banana Cards quote with custom pricing." },
      { property: "og:title", content: "Your custom quote | Banana Cards" },
      { property: "og:description", content: "Your personal Banana Cards quote with custom pricing." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  pendingComponent: QuoteSkeleton,
  errorComponent: () => <Unavailable message="We couldn't load this quote. Please try again." />,
  notFoundComponent: () => <Unavailable message="This quote doesn't exist." />,
  component: QuotePage,
});

/** Standalone page shell — no site header/footer, just the logo. */
function QuoteShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 py-6 md:px-6">
        <img src={logoSvg} alt="banana.cards" width={138} height={24} className="h-6 w-auto" />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          <BadgePercent className="h-3 w-3" /> Custom quote
        </span>
      </header>
      <main>{children}</main>
    </div>
  );
}

function Unavailable({ message }: { message: string }) {
  return (
    <QuoteShell>
      <section className="px-4 py-24 text-center md:py-32">
        <h1 className="font-display text-3xl md:text-4xl">Quote unavailable</h1>
        <p className="mt-3 text-muted-foreground">{message}</p>
        <Button asChild className="mt-8 rounded-xl">
          <Link to="/shop">Browse the shop</Link>
        </Button>
      </section>
    </QuoteShell>
  );
}

function QuoteSkeleton() {
  return (
    <QuoteShell>
      <section className="mx-auto max-w-6xl px-4 pb-16 pt-8 md:px-6">
        <Skeleton className="mx-auto h-4 w-40" />
        <Skeleton className="mx-auto mt-4 h-10 w-2/3 max-w-xl" />
        <Skeleton className="mx-auto mt-4 h-5 w-1/2 max-w-md" />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="overflow-hidden rounded-3xl bg-card">
              <Skeleton className="aspect-[5/4] w-full" />
              <div className="p-5">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="mt-2 h-6 w-3/4" />
                <Skeleton className="mt-3 h-6 w-1/2" />
                <Skeleton className="mt-4 h-10 w-full rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </section>
    </QuoteShell>
  );
}

function QuotePage() {
  const { token } = Route.useParams();
  if (!TOKEN_RE.test(token)) return <Unavailable message="This quote doesn't exist." />;
  return <QuoteContent token={token} />;
}

function QuoteContent({ token }: { token: string }) {
  const { data } = useSuspenseQuery(quoteQuery(token));
  const checkoutFn = useServerFn(createQuoteCheckout);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [draftQty, setDraftQty] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quote = data.status === "ok" ? data.quote : null;
  const country = useMemo(
    () => COUNTRIES.find((c) => c.code === quote?.countryCode) ?? DEFAULT_COUNTRY,
    [quote?.countryCode],
  );

  if (data.status === "not_found") return <Unavailable message="This quote doesn't exist." />;
  if (data.status === "unavailable") return <Unavailable message={data.message} />;
  if (!quote) return null;

  const subtotal = quote.items.reduce((s, i) => s + (qty[i.slug] ?? 0) * i.unitPrice, 0);
  const selected = quote.items.filter((i) => (qty[i.slug] ?? 0) > 0);

  const change = (slug: string, next: number, min: number, max: number | null) => {
    let v = Math.max(0, next);
    if (v > 0 && v < min) v = next < (qty[slug] ?? 0) ? 0 : min;
    if (max != null) v = Math.min(v, max);
    setQty((q) => ({ ...q, [slug]: v }));
  };

  // Typed input commit: empty/0 clears the selection, anything below min snaps
  // up to min, anything above max snaps down to max.
  const commitTyped = (slug: string, raw: string, min: number, max: number | null) => {
    const n = Number.parseInt(raw, 10);
    let v = Number.isFinite(n) && n > 0 ? n : 0;
    if (v > 0 && v < min) v = min;
    if (max != null) v = Math.min(v, max);
    setQty((q) => ({ ...q, [slug]: v }));
  };


  const buy = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await checkoutFn({
        data: { token, items: selected.map((i) => ({ slug: i.slug, qty: qty[i.slug] })) },
      });
      window.location.href = res.redirectUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <QuoteShell>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
          <div className="absolute -top-24 left-1/2 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-brand-lime/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-6 text-center md:px-6 md:pb-14">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            A personal offer{quote.customerName ? ` for ${quote.customerName}` : ""}
          </p>
          <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl leading-[1.05] tracking-tight md:text-5xl">
            {quote.title}
          </h1>
          {quote.note && (
            <p className="mx-auto mt-5 max-w-2xl whitespace-pre-line text-balance text-[15px] leading-relaxed text-muted-foreground">
              {quote.note}
            </p>
          )}
          <p className="mt-4 text-sm text-muted-foreground">
            Prices in {quote.currency}
            {quote.expiresAt ? ` · Valid until ${new Date(quote.expiresAt).toLocaleDateString()}` : ""}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Free worldwide shipping over $50</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Lifetime hardware guarantee</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> AI platform included</span>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="bg-muted">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {quote.items.map((i) => {
              const q = qty[i.slug] ?? 0;
              const off =
                i.originalPrice > i.unitPrice
                  ? Math.round((1 - i.unitPrice / i.originalPrice) * 100)
                  : 0;
              return (
                <div
                  key={i.slug}
                  className={`flex flex-col overflow-hidden rounded-3xl bg-card transition ${q > 0 ? "ring-2 ring-foreground" : ""}`}
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-background">
                    <img
                      src={IMAGES[i.slug]}
                      alt={i.name}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                    {off > 0 && (
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-background">
                        <BadgePercent className="h-3 w-3" /> {off}% off
                      </span>
                    )}
                    <span className="absolute right-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-bold tabular-nums text-foreground backdrop-blur">
                      {formatMoney(i.unitPrice, country)} / card
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <h2 className="font-display text-2xl leading-tight">{i.name}</h2>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-xl font-semibold tabular-nums">{formatMoney(i.unitPrice, country)}</span>
                      {off > 0 && (
                        <span className="text-sm text-muted-foreground line-through tabular-nums">
                          {formatMoney(i.originalPrice, country)}
                        </span>
                      )}
                      <span className="text-sm text-muted-foreground">/ card</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Min {i.minQty}{i.maxQty ? ` · Max ${i.maxQty}` : ""}
                    </p>

                    <div className="mt-auto pt-5">
                      <div className="flex items-center justify-between rounded-xl bg-muted p-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Decrease ${i.name}`}
                          onClick={() => change(i.slug, q - 1, i.minQty, i.maxQty)}
                          disabled={q === 0}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          aria-label={`Quantity for ${i.name}`}
                          value={draftQty[i.slug] ?? (q === 0 ? "" : String(q))}
                          onFocus={(e) => e.currentTarget.select()}
                          onChange={(e) => {
                            const raw = e.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 4);
                            setDraftQty((d) => ({ ...d, [i.slug]: raw }));
                          }}
                          onBlur={() => {
                            const raw = draftQty[i.slug];
                            if (raw != null) {
                              setDraftQty((d) => {
                                const next = { ...d };
                                delete next[i.slug];
                                return next;
                              });
                              commitTyped(i.slug, raw, i.minQty, i.maxQty);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          className="w-10 min-w-0 rounded-md bg-transparent text-center font-medium tabular-nums outline-none focus:bg-background"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Increase ${i.name}`}
                          onClick={() => change(i.slug, q === 0 ? i.minQty : q + 1, i.minQty, i.maxQty)}
                          disabled={i.maxQty != null && q >= i.maxQty}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STICKY SUBTOTAL BAR */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-card/95 px-4 py-4 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">Subtotal (shipping calculated at checkout)</p>
            <p className="text-2xl font-semibold tabular-nums">{formatMoney(subtotal, country)}</p>
            {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
          </div>
          <Button size="lg" className="rounded-xl" disabled={selected.length === 0 || busy} onClick={buy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Buy now
          </Button>
        </div>
      </div>
      {/* Spacer so the sticky bar never covers content */}
      <div className="h-28" />
    </QuoteShell>
  );
}
